import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { type SxProps, styled, type Theme, useTheme } from '@mui/material/styles';
import type { ApexOptions } from 'apexcharts';
import { lazy, Suspense } from 'react';

/**
 * The Minimal kit's `components/chart`, copied into `packages/ui` and converted
 * to TypeScript — .claude/rules/ui-copy-never-import.md, and the chart item of
 * docs/plans/07-notifications-and-reporting.md §7.3.
 *
 * **ApexCharts is loaded lazily and nowhere else.** `react-apexcharts` pulls in
 * the whole of `apexcharts`, which is large and which only the reporting screens
 * want; `lazy()` keeps it out of every other route's chunk, exactly as the kit
 * does. The `Suspense` fallback is a skeleton the size of the chart, so a screen
 * does not jump when the bundle lands.
 *
 * The kit's `classes.js`, its `styles.css` and its legend components are
 * deliberately **not** copied: nothing here styles a chart by class name, and a
 * legend is `Typography` beside the chart on the screens that want one.
 */
export type ChartType = NonNullable<ApexOptions['chart']>['type'];

export type ChartProps = {
  type: ChartType;
  series: ApexOptions['series'];
  options?: ApexOptions;
  height?: number | string;
  sx?: SxProps<Theme>;
};

const LazyApexChart = lazy(() => import('react-apexcharts'));

const ChartRoot = styled('div')(({ theme }) => ({
  width: '100%',
  flexShrink: 0,
  position: 'relative',
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
}));

export function Chart({ type, series, options = {}, height = 320, sx }: ChartProps) {
  return (
    <ChartRoot dir="ltr" sx={{ height, ...sx }}>
      <Suspense
        fallback={
          <Box sx={{ height, p: 1 }}>
            <Skeleton variant="rounded" sx={{ height: '100%' }} />
          </Box>
        }
      >
        <LazyApexChart type={type} series={series} options={options} width="100%" height="100%" />
      </Suspense>
    </ChartRoot>
  );
}

/**
 * The kit's `useChart`: the theme's own colours, fonts and dividers as the
 * chart's defaults, so a chart looks like the rest of the panel without every
 * screen restating it. A caller's options are merged **over** these, one level
 * deep per section, which is what every chart here needs — the kit merges
 * deeply with `es-toolkit`, a dependency this package does not carry.
 */
export function useChartOptions(overrides: ApexOptions = {}): ApexOptions {
  const theme = useTheme();
  const base: ApexOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      parentHeightOffset: 0,
      fontFamily: theme.typography.fontFamily,
      foreColor: theme.palette.text.disabled,
      animations: { enabled: true, speed: 360 },
    },
    colors: [
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.error.main,
      theme.palette.success.main,
      theme.palette.warning.dark,
      theme.palette.info.dark,
    ],
    states: { hover: { filter: { type: 'darken' } }, active: { filter: { type: 'darken' } } },
    fill: { opacity: 1 },
    dataLabels: { enabled: false },
    stroke: { width: 2.5, curve: 'smooth', lineCap: 'round' },
    grid: {
      strokeDashArray: 3,
      borderColor: theme.palette.divider,
      padding: { top: 0, right: 0, bottom: 0 },
      xaxis: { lines: { show: false } },
    },
    xaxis: { axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { tickAmount: 5 },
    markers: { size: 0, strokeColors: theme.palette.background.paper },
    tooltip: { fillSeriesColor: false, x: { show: true } },
    legend: {
      show: false,
      position: 'top',
      fontWeight: 500,
      fontSize: '13px',
      horizontalAlign: 'right',
      labels: { colors: theme.palette.text.primary },
      itemMargin: { horizontal: 8, vertical: 8 },
    },
    plotOptions: {
      bar: { borderRadius: 4, columnWidth: '48%', borderRadiusApplication: 'end' },
      radialBar: { hollow: { margin: -8, size: '68%' }, track: { margin: -8, strokeWidth: '50%' } },
    },
  };

  return {
    ...base,
    ...overrides,
    chart: { ...base.chart, ...overrides.chart },
    grid: { ...base.grid, ...overrides.grid },
    xaxis: { ...base.xaxis, ...overrides.xaxis },
    yaxis: Array.isArray(overrides.yaxis) ? overrides.yaxis : { ...base.yaxis, ...overrides.yaxis },
    legend: { ...base.legend, ...overrides.legend },
    plotOptions: { ...base.plotOptions, ...overrides.plotOptions },
    tooltip: { ...base.tooltip, ...overrides.tooltip },
  };
}
